import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, catchError, map, of, shareReplay, tap } from 'rxjs';
import { DiApiService } from './di-api.service';
import { DiEnvironment } from './di.enums';
import { DiSettings } from './di.models';

@Injectable({ providedIn: 'root' })
export class DiSettingsCacheService {
  private readonly api = inject(DiApiService);
  private readonly settingsSubject = new BehaviorSubject<DiSettings | null>(null);
  private readonly fbrSandboxSubject = new BehaviorSubject<boolean | null>(null);
  private loaded = false;
  private loading$: Observable<DiSettings | null> | null = null;

  getSettings(force = false): Observable<DiSettings | null> {
    if (this.loaded && !force) {
      return of(this.settingsSubject.value);
    }
    if (!this.loading$ || force) {
      this.loading$ = this.api.getSettings().pipe(
        map((resp) => resp?.data ?? null),
        tap((settings) => this.publish(settings)),
        catchError(() => {
          this.publish(null);
          return of(null);
        }),
        shareReplay(1)
      );
    }
    return this.loading$;
  }

  /** null until settings load. True when FBR environment is Sandbox. */
  isFbrSandbox$(): Observable<boolean | null> {
    if (!this.loaded) {
      this.getSettings().subscribe();
    }
    return this.fbrSandboxSubject.asObservable();
  }

  applyLocal(settings: DiSettings | null): void {
    this.publish(settings);
  }

  private publish(settings: DiSettings | null): void {
    this.loaded = true;
    this.loading$ = null;
    this.settingsSubject.next(settings);
    this.fbrSandboxSubject.next(Number(settings?.fbrEnvironment) === DiEnvironment.Sandbox);
  }
}
